import React, { useState, useRef, useEffect } from 'react';
import { audioFilesService } from '../../services/audioFiles';
import { AudioFile } from '../../types';
import { formatDuration } from '../../utils/formatters';
import { usePictureInPicture } from '../../hooks/usePictureInPicture';
import { useTutorial } from '../../hooks/useTutorial';
import TutorialManager from '../tutorial/TutorialManager';
import { mediaPlayerTutorial } from '../tutorial/tutorials/MediaPlayerTutorial';
import {
  PlayIcon,
  PauseIcon,
  SkipBackwardIcon,
  SkipForwardIcon,
  VolumeIcon,
  VolumeOffIcon,
  PictureInPictureIcon,
  ErrorIcon,
  AudioWaveLoadingIcon,
} from '../../icons';

interface MediaPlayerProps {
  mediaFile: AudioFile;
  transcriptCurrentTime: number | undefined;
  transcriptIsPlaying: boolean | undefined;
  onTranscriptPlaybackUpdate: (timestamp: number, playing: boolean) => void;
  onTranscriptContinousUpdate: (timestamp: number, playing: boolean) => void;
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({
  mediaFile,
  transcriptCurrentTime,
  transcriptIsPlaying,
  onTranscriptPlaybackUpdate,
  onTranscriptContinousUpdate,
}) => {
  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement>(null);
  const isProcessingTranscriptUpdate = useRef(false);
  const [, forceUpdate] = useState(0); // Dummy state to trigger re-renders
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Determine if this is a video file based on content type
  const isVideo = mediaFile?.contentType?.startsWith('video/');

  // Picture-in-Picture support
  const { isPiPActive, isPiPSupported, togglePiP } = usePictureInPicture(
    mediaRef as React.RefObject<HTMLVideoElement>,
    isVideo,
    mediaUrl
  );

  // Trigger media player tutorial when file is uploaded
  useTutorial({
    tutorialId: 'media-player',
    trigger: !!mediaFile && !mediaFile.hasTranscript,
    delay: 1000,
  });

  // Load media URL when component mounts or mediaFile changes
  useEffect(() => {
    const loadMediaUrl = async () => {
      if (!mediaFile) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await audioFilesService.getDownloadUrl(mediaFile.id);
        setMediaUrl(data.download_url);
      } catch (err) {
        setError(`Failed to load ${isVideo ? 'video' : 'audio'} file`);
        console.error('Error loading media URL:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadMediaUrl();
  }, [mediaFile, isVideo]);

  // Reset player state when media file changes
  useEffect(() => {
    setDuration(0);

    if (mediaRef.current) {
      mediaRef.current.pause();
      mediaRef.current.currentTime = 0;
    }
  }, [mediaFile]);

  // Media event handlers
  const handleLoadedMetadata = () => {
    if (mediaRef.current) {
      setDuration(mediaRef.current.duration);
    }
  };

  // Sync with external state
  useEffect(() => {
    if (
      !mediaRef.current ||
      (transcriptCurrentTime === undefined && transcriptIsPlaying === undefined)
    )
      return;

    // Mark that we're processing a transcript update
    isProcessingTranscriptUpdate.current = true;

    if (
      transcriptCurrentTime !== undefined &&
      Math.abs(mediaRef.current.currentTime - transcriptCurrentTime) > 0.1
    ) {
      mediaRef.current.currentTime = transcriptCurrentTime;
    }

    if (transcriptIsPlaying !== undefined) {
      if (transcriptIsPlaying && mediaRef.current.paused) {
        mediaRef.current.play().catch(console.error);
      } else if (!transcriptIsPlaying && !mediaRef.current.paused) {
        mediaRef.current.pause();
      }
    }

    // Clear the flag after a delay to allow all events to process
    setTimeout(() => {
      isProcessingTranscriptUpdate.current = false;
    }, 300);
  }, [transcriptCurrentTime, transcriptIsPlaying]);

  const handlePlayPauseClick = () => {
    if (!mediaRef.current || !mediaUrl) return;

    const isPlaying = !mediaRef.current?.paused;

    if (isPlaying) {
      mediaRef.current.pause();
    } else {
      mediaRef.current.play().catch((err) => {
        console.error('Error playing media:', err);
        setError(`Failed to play ${isVideo ? 'video' : 'audio'}`);
      });
    }

    // Call user intervention callback immediately
    onTranscriptPlaybackUpdate(mediaRef.current.currentTime, !isPlaying);
  };

  const handlePlay = () => {
    if (!mediaRef.current || !mediaUrl) return;

    // Don't send updates if this was triggered by a transcript update
    if (!isProcessingTranscriptUpdate.current) {
      onTranscriptPlaybackUpdate(mediaRef.current.currentTime, true);
    }
  };

  const handlePause = () => {
    if (!mediaRef.current || !mediaUrl) return;

    // Don't send updates if this was triggered by a transcript update
    if (!isProcessingTranscriptUpdate.current) {
      onTranscriptPlaybackUpdate(mediaRef.current.currentTime, false);
    }
  };

  const handleEnded = () => {
    handlePause();
  };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef.current || !mediaUrl) return;

    const timestamp = parseFloat(e.target.value);
    handleTimeChange(timestamp);
  };

  const handleTimeChange = (timestamp: number) => {
    if (!mediaRef.current || !mediaUrl) return;

    const isPlaying = !mediaRef.current.paused;
    mediaRef.current.currentTime = timestamp;

    // Call user intervention callback immediately
    onTranscriptPlaybackUpdate(timestamp, isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!mediaRef.current || !mediaUrl) return;

    const isPlaying = !mediaRef.current.paused;
    const timestamp = mediaRef.current.currentTime;

    // Don't send updates if we're processing a transcript update
    if (isProcessingTranscriptUpdate.current) {
      forceUpdate((n) => n + 1); // Trigger re-render for UI update
      return;
    }

    // forceUpdate((n) => n + 1); // Trigger re-render for UI update
    onTranscriptContinousUpdate(timestamp, isPlaying);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (mediaRef.current) {
      mediaRef.current.volume = newVolume;
    }
    // If user changes volume while muted, unmute
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }
    // If user sets volume to 0, consider it muted
    if (newVolume === 0 && !isMuted) {
      setIsMuted(true);
    }
  };

  const handleMuteToggle = () => {
    if (isMuted) {
      // Unmute: restore previous volume
      const volumeToRestore = previousVolume > 0 ? previousVolume : 0.5;
      setVolume(volumeToRestore);
      setIsMuted(false);
      if (mediaRef.current) {
        mediaRef.current.volume = volumeToRestore;
      }
    } else {
      // Mute: store current volume and set to 0
      setPreviousVolume(volume);
      setVolume(0);
      setIsMuted(true);
      if (mediaRef.current) {
        mediaRef.current.volume = 0;
      }
    }
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(newRate);
    if (mediaRef.current) {
      mediaRef.current.playbackRate = newRate;
    }
  };

  const getProgressPercentage = () => {
    if (duration === 0 || !mediaRef.current) return 0;
    return (mediaRef.current.currentTime / duration) * 100;
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-center space-x-2">
          <AudioWaveLoadingIcon size={16} className="text-blue-600" />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Loading {isVideo ? 'video' : 'audio'}...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-red-600">
          <ErrorIcon size={16} />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (!mediaUrl) {
    return null;
  }

  const isPlaying = !mediaRef.current?.paused;
  const durationDisplay = formatDuration(duration);
  const currentTimeDisplay = formatDuration(mediaRef.current?.currentTime || 0);

  return (
    <div
      className={`relative border rounded-lg p-4 transition-all duration-500 max-w-full overflow-hidden ${
        isPlaying
          ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 border-amber-400 dark:border-amber-600 shadow-xl shadow-amber-200/50 dark:shadow-amber-900/50'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Video element (if video file) */}
      {isVideo ? (
        <div
          className={
            isPiPActive ? 'h-0 overflow-hidden' : 'max-w-full overflow-hidden'
          }
        >
          <video
            ref={mediaRef as React.RefObject<HTMLVideoElement>}
            src={mediaUrl}
            className="w-full max-h-96 bg-black rounded-lg mb-3 object-contain"
            controls={false}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onError={() => setError('Error loading video file')}
            preload="metadata"
          />
        </div>
      ) : (
        <audio
          ref={mediaRef as React.RefObject<HTMLAudioElement>}
          src={mediaUrl}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={() => setError('Error loading audio file')}
          preload="metadata"
        />
      )}

      {/* First Row: Controls, Volume, Timers, Speed */}
      <div className="relative z-10 mb-3 space-y-3">
        {/* Top Row: Playback Controls (centered on mobile) */}
        <div className="flex items-center justify-between">
          {/* Left: Volume Control (hidden on mobile) */}
          <div
            className="hidden sm:flex items-center space-x-2"
            data-tutorial="volume-control"
          >
            <button
              onClick={handleMuteToggle}
              className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors rounded"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeOffIcon size={20} className="text-red-500" />
              ) : (
                <VolumeIcon size={20} />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={handleVolumeChange}
              className="w-20 h-1 bg-gray-200 dark:bg-gray-600 rounded-lg cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${volume * 100}%, #E5E7EB ${volume * 100}%, #E5E7EB 100%)`,
              }}
            />
          </div>

          {/* Center: Play/Pause and Skip Controls */}
          <div
            className="flex items-center space-x-2 sm:space-x-3 mx-auto sm:mx-0"
            data-tutorial="playback-controls"
          >
            {/* Skip Backward */}
            <button
              onClick={() =>
                handleTimeChange(
                  Math.max(0, (mediaRef.current?.currentTime || 0) - 10)
                )
              }
              className="p-1.5 sm:p-2.5 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
              title="Skip back 10s"
            >
              <SkipBackwardIcon size={22} className="sm:w-[26px] sm:h-[26px]" />
            </button>

            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPauseClick}
              className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
              disabled={!mediaUrl}
            >
              {isPlaying ? (
                <PauseIcon size={18} className="sm:w-[21px] sm:h-[21px]" />
              ) : (
                <PlayIcon size={18} className="sm:w-[21px] sm:h-[21px]" />
              )}
            </button>

            {/* Skip Forward */}
            <button
              onClick={() =>
                handleTimeChange(
                  Math.min(duration, (mediaRef.current?.currentTime || 0) + 10)
                )
              }
              className="p-1.5 sm:p-2.5 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
              title="Skip forward 10s"
            >
              <SkipForwardIcon size={22} className="sm:w-[26px] sm:h-[26px]" />
            </button>
          </div>

          {/* Right: Timers and Speed (hidden on mobile) */}
          <div className="hidden sm:flex items-center space-x-4">
            {/* Time Display */}
            <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400 min-w-0">
              <span
                className="tabular-nums text-right inline-block"
                style={{ minWidth: `${durationDisplay.length}ch` }}
              >
                {currentTimeDisplay}
              </span>
              <span>/</span>
              <span className="tabular-nums">{durationDisplay}</span>
            </div>

            {/* Speed Control */}
            <select
              data-tutorial="playback-speed"
              value={playbackRate}
              onChange={handleSpeedChange}
              className="text-sm bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200"
            >
              <option value="0.5">0.5×</option>
              <option value="0.75">0.75×</option>
              <option value="1">1×</option>
              <option value="1.25">1.25×</option>
              <option value="1.5">1.5×</option>
              <option value="2">2×</option>
            </select>

            {/* Picture-in-Picture Button (for video only) */}
            {isVideo && isPiPSupported && (
              <button
                onClick={togglePiP}
                className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors rounded"
                title={
                  isPiPActive ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'
                }
                data-tutorial="picture-in-picture"
              >
                <PictureInPictureIcon
                  size={20}
                  className={isPiPActive ? 'text-blue-600' : ''}
                />
              </button>
            )}
          </div>
        </div>

        {/* Mobile-only bottom row: Time, Speed, Volume */}
        <div className="sm:hidden flex items-center justify-between text-xs">
          {/* Time Display */}
          <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
            <span className="tabular-nums">{currentTimeDisplay}</span>
            <span>/</span>
            <span className="tabular-nums">{durationDisplay}</span>
          </div>

          {/* Speed and Volume Controls */}
          <div className="flex items-center space-x-3">
            {/* Speed Control */}
            <select
              data-tutorial="playback-speed"
              value={playbackRate}
              onChange={handleSpeedChange}
              className="text-xs bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200"
            >
              <option value="0.5">0.5×</option>
              <option value="0.75">0.75×</option>
              <option value="1">1×</option>
              <option value="1.25">1.25×</option>
              <option value="1.5">1.5×</option>
              <option value="2">2×</option>
            </select>

            {/* Volume Control */}
            <button
              onClick={handleMuteToggle}
              className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors rounded"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeOffIcon size={16} className="text-red-500" />
              ) : (
                <VolumeIcon size={16} />
              )}
            </button>

            {/* Picture-in-Picture Button (for video only) */}
            {isVideo && isPiPSupported && (
              <button
                onClick={togglePiP}
                className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors rounded"
                title={
                  isPiPActive ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'
                }
                data-tutorial="picture-in-picture"
              >
                <PictureInPictureIcon
                  size={16}
                  className={isPiPActive ? 'text-blue-600' : ''}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Second Row: Progress Bar */}
      <div className="relative">
        <input
          data-tutorial="timeline"
          type="range"
          min="0"
          max={duration || 0}
          value={mediaRef.current?.currentTime || 0}
          onChange={handleSeekChange}
          className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${getProgressPercentage()}%, #E5E7EB ${getProgressPercentage()}%, #E5E7EB 100%)`,
          }}
        />
      </div>

      <TutorialManager tutorials={[mediaPlayerTutorial]} />
    </div>
  );
};

export default MediaPlayer;
